import "../foundation/ctl-skeleton.js";
import "../foundation/ctl-spinner.js";
import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    display:block;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    color: var(--ctl-color-text);
  }

  .frame{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
  }

  .toolbar{
    padding: var(--ctl-space-3) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  .toolbar:empty{ display:none; }

  .tableWrap{
    position: relative;
    overflow: auto;
    background: var(--ctl-color-surface);
  }

  table{
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: var(--ctl-table-min-width);
  }

  thead th{
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--ctl-color-surface);
    border-bottom: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    text-align: left;
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    white-space: nowrap;
  }

  th, td{
    padding: var(--ctl-space-3) var(--ctl-space-3);
    border-bottom: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    vertical-align: middle;
  }

  :host([density="compact"]) th,
  :host([density="compact"]) td{
    padding: var(--ctl-space-2) var(--ctl-space-3);
  }

  tbody tr{
    background: var(--ctl-color-surface);
    transition: background var(--ctl-transition-fast);
  }

  tbody tr:hover{
    background: color-mix(in srgb, var(--ctl-color-brand) 6%, var(--ctl-color-surface));
  }

  tbody tr[data-clickable="true"]{
    cursor: pointer;
  }

  .cell{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
  }

  .muted{
    color: var(--ctl-color-muted);
  }

  .right{ text-align:right; }
  .center{ text-align:center; }

  /* Sort button */
  .sortBtn{
    all: unset;
    cursor: pointer;
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-2) var(--ctl-space-2);
    margin: calc(-1 * var(--ctl-space-2)) calc(-1 * var(--ctl-space-2));
    border-radius: var(--ctl-radius-md);
  }
  .sortBtn:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 35%, transparent);
  }
  .sortBtn:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .sortIcon{
    width: 14px;
    height: 14px;
    color: var(--ctl-color-muted);
    opacity: .9;
    display:inline-flex;
    align-items:center;
    justify-content:center;
  }

  /* Checkbox */
  .chk{
    width: var(--ctl-size-icon-sm);
    height: var(--ctl-size-icon-sm);
    accent-color: var(--ctl-color-brand);
  }

  .selectCol{
    width: 44px;
  }

  /* Empty */
  .empty{
    padding: var(--ctl-space-4);
    color: var(--ctl-color-muted);
    text-align: center;
  }

  /* When skeleton mode is on, reduce overlay intensity */
  :host([loading][skeleton]) .loading{
    background: color-mix(in srgb, var(--ctl-color-bg) 25%, transparent);
    backdrop-filter: none;
  }

  td ctl-skeleton{
    display:block;
  }

  /* Loading overlay */
  .loading{
    position: absolute;
    inset: 0;
    display: none;
    align-items:center;
    justify-content:center;
    background: color-mix(in srgb, var(--ctl-color-bg) 65%, transparent);
    backdrop-filter: blur(1px);
    z-index: 2;
  }
  :host([loading]) .loading{ display:flex; }

  .spinner{
    width: var(--ctl-space-6); height: var(--ctl-space-6);
    border-radius: var(--ctl-radius-full);
    border: var(--ctl-border-width-thick) solid color-mix(in srgb, var(--ctl-color-border-2) 85%, transparent);
    border-top-color: var(--ctl-color-brand);
    animation: spin .8s linear infinite;
  }
  @keyframes spin{ to{ transform: rotate(360deg); } }

  /* Hide light-dom templates used for render hooks */
  ::slotted(template[slot="cell"]),
  ::slotted(template[slot="header-cell"]){
    display:none;
  }

  /* Responsive table - stacked card view on mobile */
  @media (max-width: 768px) {
    :host([responsive]) table {
      border: 0;
    }

    :host([responsive]) thead {
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }

    :host([responsive]) tbody tr {
      display: block;
      margin-bottom: var(--ctl-space-3);
      border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
      border-radius: var(--ctl-radius-md);
      background: var(--ctl-color-surface);
    }

    :host([responsive]) tbody tr:hover {
      background: var(--ctl-color-surface);
    }

    :host([responsive]) tbody td {
      display: block;
      text-align: right;
      padding: var(--ctl-space-2-5) var(--ctl-space-3);
      border-bottom: var(--ctl-border-width-thin) solid color-mix(in srgb, var(--ctl-color-border) 50%, transparent);
    }

    :host([responsive]) tbody td:last-child {
      border-bottom: 0;
    }

    :host([responsive]) tbody td::before {
      content: attr(data-label);
      float: left;
      font-weight: var(--ctl-font-weight-semibold);
      text-transform: uppercase;
      font-size: var(--ctl-font-size-xs);
      color: var(--ctl-color-muted);
      letter-spacing: var(--ctl-tracking-wide);
    }

    /* Hide checkbox column label on mobile */
    :host([responsive]) tbody td.selectCol::before {
      content: '';
    }
  }

  /* Horizontal scroll variant for mobile */
  @media (max-width: 768px) {
    :host([scroll-on-mobile]) table {
      min-width: 600px;
    }

    /* Scroll indicator */
    :host([scroll-on-mobile]) .tableWrap::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: var(--ctl-space-10);
      background: linear-gradient(to left, var(--ctl-color-surface), transparent);
      pointer-events: none;
    }
  }

</style>

<div class="frame" part="frame">
  <div class="toolbar" part="toolbar"><slot name="toolbar"></slot></div>

  <div class="tableWrap" part="tableWrap" aria-busy="false">
    <div class="loading" part="loading" aria-hidden="true">
      <ctl-spinner size="md" label="Loading…" part="spinner"></ctl-spinner>
    </div>

    <table part="table">
      <thead part="thead">
        <tr id="headRow"></tr>
      </thead>
      <tbody id="body"></tbody>
    </table>

    <div id="empty" class="empty" part="empty" hidden>
      <slot name="empty">No results.</slot>
    </div>

    <slot id="cellTemplatesSlot" name="cell"></slot>
    <slot id="headerTemplatesSlot" name="header-cell"></slot>
  </div>
</div>
`;

function toArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeSort(v) {
  if (!v || typeof v !== "object") return null;
  const id = String(v.id || "");
  const dir = v.dir === "desc" ? "desc" : (v.dir === "asc" ? "asc" : "asc");
  return id ? { id, dir } : null;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function valueFor(row, colId) {
  if (!row) return "";
  const v = row[colId];
  return v == null ? "" : v;
}

export class CtlTable extends HTMLElement {
  static get observedAttributes() {
    return ["selectable", "density", "loading", "skeleton", "skeleton-rows", "columns", "rows", "sort", "selected"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._headRow = this.shadowRoot.getElementById("headRow");
    this._body = this.shadowRoot.getElementById("body");
    this._empty = this.shadowRoot.getElementById("empty");
    this._wrap = this.shadowRoot.querySelector(".tableWrap");

    this._cellTemplatesSlot = this.shadowRoot.getElementById("cellTemplatesSlot");
    this._headerTemplatesSlot = this.shadowRoot.getElementById("headerTemplatesSlot");

    this._columns = [];
    this._rows = [];
    this._sort = null;
    this._selected = [];

    this._cellTemplates = new Map();   // key: columnId or "*" -> HTMLTemplateElement
    this._headerTemplates = new Map(); // key: columnId or "*" -> HTMLTemplateElement

    // Flag to prevent infinite loops when syncing props with attributes
    this._updatingFromAttribute = false;

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("density")) this.setAttribute("density", "comfortable");

    // Initial attribute hydration (if user provided JSON attrs)
    if (this.hasAttribute("columns")) this.columns = safeJsonParse(this.getAttribute("columns"), []);
    if (this.hasAttribute("rows")) this.rows = safeJsonParse(this.getAttribute("rows"), []);
    if (this.hasAttribute("sort")) this.sort = safeJsonParse(this.getAttribute("sort"), null);
    if (this.hasAttribute("selected")) this.selected = safeJsonParse(this.getAttribute("selected"), []);

    this._cellTemplatesSlot.addEventListener("slotchange", this._onSlotChange);
    this._headerTemplatesSlot.addEventListener("slotchange", this._onSlotChange);

    this.shadowRoot.addEventListener("click", this._onClick);
    this.shadowRoot.addEventListener("change", this._onChange);

    this._collectTemplates();
    this._render();
  }

  disconnectedCallback() {
    this._cellTemplatesSlot.removeEventListener("slotchange", this._onSlotChange);
    this._headerTemplatesSlot.removeEventListener("slotchange", this._onSlotChange);
    this.shadowRoot.removeEventListener("click", this._onClick);
    this.shadowRoot.removeEventListener("change", this._onChange);
  }

  attributeChangedCallback(name) {
    // keep in sync when user sets attrs directly
    // Use flag to prevent infinite loops
    this._updatingFromAttribute = true;
    try {
      if (name === "columns") this.columns = safeJsonParse(this.getAttribute("columns"), []);
      if (name === "rows") this.rows = safeJsonParse(this.getAttribute("rows"), []);
      if (name === "sort") this.sort = safeJsonParse(this.getAttribute("sort"), null);
      if (name === "selected") this.selected = safeJsonParse(this.getAttribute("selected"), []);
    } finally {
      this._updatingFromAttribute = false;
    }
    
    if (name === "loading") this._syncLoading();
    if (name === "density") this._render(); // cheap enough
    if (name === "selectable") this._render();
    if (name === "skeleton" || name === "skeleton-rows") this._renderBody();
  }

  // ---- Props ----
  get columns() { return this._columns; }
  set columns(v) {
    this._columns = toArray(v).map((c) => ({
      id: String(c?.id ?? ""),
      label: String(c?.label ?? ""),
      sortable: !!c?.sortable,
      width: c?.width != null ? String(c.width) : null,
      align: c?.align === "right" ? "right" : (c?.align === "center" ? "center" : "left")
    })).filter((c) => c.id);

    // Optional reflect as JSON (keeps your API consistent)
    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("columns", JSON.stringify(this._columns));
    }

    this._render();
  }

  get rows() { return this._rows; }
  set rows(v) {
    this._rows = toArray(v).map((r) => {
      const obj = (r && typeof r === "object") ? r : {};
      return { ...obj, id: String(obj.id ?? "") };
    }).filter((r) => r.id);

    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("rows", JSON.stringify(this._rows));
    }
    this._render();
  }

  get sort() { return this._sort; }
  set sort(v) {
    this._sort = normalizeSort(v);
    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("sort", JSON.stringify(this._sort));
    }
    this._render();
  }

  get selectable() { return this.hasAttribute("selectable"); }
  set selectable(v) { v ? this.setAttribute("selectable", "") : this.removeAttribute("selectable"); }

  get selected() { return this._selected; }
  set selected(v) {
    this._selected = toArray(v).map((x) => String(x));
    // Only update attribute if not already updating from attribute (prevent infinite loop)
    if (!this._updatingFromAttribute) {
      this.setAttribute("selected", JSON.stringify(this._selected));
    }
    this._renderBody(); // no need to rerender head each time
  }

  get density() { return this.getAttribute("density") || "comfortable"; }
  set density(v) {
    const next = (v === "compact") ? "compact" : "comfortable";
    this.setAttribute("density", next);
  }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  // ---- Slots: template render hooks ----
  _onSlotChange() {
    this._collectTemplates();
    this._render();
  }

  _collectTemplates() {
    this._cellTemplates.clear();
    this._headerTemplates.clear();

    const cellTmpls = this._cellTemplatesSlot.assignedElements({ flatten: true }) || [];
    for (const el of cellTmpls) {
      if (el.tagName !== "TEMPLATE") continue;
      const key = el.getAttribute("data-column") || "*";
      this._cellTemplates.set(key, el);
    }

    const headerTmpls = this._headerTemplatesSlot.assignedElements({ flatten: true }) || [];
    for (const el of headerTmpls) {
      if (el.tagName !== "TEMPLATE") continue;
      const key = el.getAttribute("data-column") || "*";
      this._headerTemplates.set(key, el);
    }
  }

  // ---- Render ----
  _render() {
    this._syncLoading();
    this._renderHead();
    this._renderBody();
  }

  _syncLoading() {
    this._wrap.setAttribute("aria-busy", this.loading ? "true" : "false");
  }

  _renderHead() {
    const cols = this._columns;
    const selectable = this.selectable;

    const ths = [];

    if (selectable) {
      const allChecked = this._rows.length > 0 && this._rows.every((r) => this._selected.includes(r.id));
      const someChecked = this._rows.some((r) => this._selected.includes(r.id)) && !allChecked;

      ths.push(`
        <th class="selectCol center" scope="col">
          <input
            class="chk"
            type="checkbox"
            data-role="select-all"
            aria-label="Select all rows"
            ${allChecked ? "checked" : ""}
          />
        </th>
      `);

      // Indeterminate must be set via JS after render
      queueMicrotask(() => {
        const el = this.shadowRoot.querySelector('input[data-role="select-all"]');
        if (el) el.indeterminate = someChecked;
      });
    }

    for (const col of cols) {
      const style = [
        col.width ? `width:${escapeHtml(col.width)};` : "",
        col.align === "right" ? "text-align:right;" : (col.align === "center" ? "text-align:center;" : "")
      ].filter(Boolean).join("");

      const isSorted = this._sort && this._sort.id === col.id;
      const dir = isSorted ? this._sort.dir : null;

      // Header template hook
      const headerTmpl = this._headerTemplates.get(col.id) || this._headerTemplates.get("*");

      let contentHtml = "";
      if (headerTmpl) {
        contentHtml = this._renderTemplate(headerTmpl, {
          columnId: col.id,
          label: col.label,
          sortable: col.sortable ? "true" : "false",
          sortDir: dir || ""
        });
      } else {
        contentHtml = `<span>${escapeHtml(col.label)}</span>`;
      }

      if (col.sortable) {
        const icon = dir === "asc"
          ? "▲"
          : dir === "desc"
            ? "▼"
            : "↕";

        ths.push(`
          <th scope="col" style="${style}" data-col="${escapeHtml(col.id)}">
            <button class="sortBtn" type="button" data-role="sort" data-col="${escapeHtml(col.id)}" aria-label="Sort ${escapeHtml(col.label)}">
              ${contentHtml}
              <span class="sortIcon" aria-hidden="true">${icon}</span>
            </button>
          </th>
        `);
      } else {
        ths.push(`
          <th scope="col" style="${style}" data-col="${escapeHtml(col.id)}">
            ${contentHtml}
          </th>
        `);
      }
    }

    this._headRow.innerHTML = ths.join("");
  }

  _renderBody() {
    const cols = this._columns;
    const selectable = this.selectable;

    
        // If loading + skeleton, render skeleton rows (even if rows are empty)
    if (this.loading && this.skeleton) {
      this._empty.hidden = true;
      this.shadowRoot.querySelector("table").style.display = "table";

      const count = this.skeletonRows;

      const widthFor = (i, colId) => {
        // deterministic “variety” without randomness
        const m = (i + (colId ? colId.length : 0)) % 3;
        return m === 0 ? "38%" : (m === 1 ? "62%" : "82%");
      };

      const skeletonForCol = (i, col) => {
        const id = (col?.id || "").toLowerCase();
        const isActionish =
          id.includes("action") || id.includes("menu") || id.includes("status") || id.includes("badge");

        if (isActionish) {
          return `<ctl-skeleton variant="rect" width="84px" height="28px"></ctl-skeleton>`;
        }

        // If the column has an explicit width, we still render a text skeleton that fills the cell.
        // Using percentage widths keeps it looking natural.
        const w = widthFor(i, col.id);
        return `<ctl-skeleton variant="text" width="${w}" height="12px"></ctl-skeleton>`;
      };

      const rowsHtml = Array.from({ length: count }).map((_, i) => {
        const tds = [];

        if (selectable) {
          tds.push(`
            <td class="center">
              <ctl-skeleton variant="rect" width="18px" height="18px"></ctl-skeleton>
            </td>
          `);
        }

        for (const col of cols) {
          const alignClass = col.align === "right" ? "right" : (col.align === "center" ? "center" : "");
          tds.push(`
            <td class="${alignClass}">
              <div class="cell">
                ${skeletonForCol(i, col)}
              </div>
            </td>
          `);
        }

        return `<tr aria-hidden="true">${tds.join("")}</tr>`;
      }).join("");

      this._body.innerHTML = rowsHtml;
      return;
    }


    // Normal path (your existing logic)
    const rows = this._getSortedRows();

    // Empty state: only when not loading
    const isEmpty = rows.length === 0 && !this.loading;
    this._empty.hidden = !isEmpty;
    this.shadowRoot.querySelector("table").style.display = isEmpty ? "none" : "table";

    if (isEmpty) {
      this._body.innerHTML = "";
      return;
    }

    const html = rows.map((row) => {
      const isChecked = this._selected.includes(row.id);

      const tds = [];

      if (selectable) {
        tds.push(`
          <td class="center">
            <input
              class="chk"
              type="checkbox"
              data-role="row-check"
              data-row="${escapeHtml(row.id)}"
              aria-label="Select row ${escapeHtml(row.id)}"
              ${isChecked ? "checked" : ""}
            />
          </td>
        `);
      }

      for (const col of cols) {
        const alignClass = col.align === "right" ? "right" : (col.align === "center" ? "center" : "");
        const raw = valueFor(row, col.id);

        const cellTmpl = this._cellTemplates.get(col.id) || this._cellTemplates.get("*");

        let cellInner = "";
        if (cellTmpl) {
          cellInner = this._renderTemplate(cellTmpl, {
            ...row,
            rowId: row.id,
            columnId: col.id,
            value: raw,
            valueText: String(raw ?? ""),
            row: JSON.stringify(row)
          });
        } else {
          cellInner = `<span>${escapeHtml(String(raw ?? ""))}</span>`;
        }

        tds.push(`
          <td class="${alignClass}" data-row="${escapeHtml(row.id)}" data-col="${escapeHtml(col.id)}">
            <div class="cell">
              ${cellInner}
            </div>
          </td>
        `);
      }

      return `
        <tr data-row="${escapeHtml(row.id)}" data-clickable="true">
          ${tds.join("")}
        </tr>
      `;
    }).join("");

    this._body.innerHTML = html;
  }

  _getSortedRows() {
    const rows = [...this._rows];
    const s = this._sort;

    if (!s || !s.id) return rows;

    const col = this._columns.find((c) => c.id === s.id);
    if (!col || !col.sortable) return rows;

    const dirMul = s.dir === "desc" ? -1 : 1;

    rows.sort((a, b) => {
      const av = valueFor(a, s.id);
      const bv = valueFor(b, s.id);

      // compare numbers when both numeric
      const an = Number(av);
      const bn = Number(bv);
      const bothNumeric = Number.isFinite(an) && Number.isFinite(bn) && String(av).trim() !== "" && String(bv).trim() !== "";

      let cmp = 0;
      if (bothNumeric) {
        cmp = an === bn ? 0 : (an < bn ? -1 : 1);
      } else {
        const as = String(av ?? "").toLowerCase();
        const bs = String(bv ?? "").toLowerCase();
        cmp = as.localeCompare(bs);
      }
      return cmp * dirMul;
    });

    return rows;
  }

  _renderTemplate(tmpl, data) {
    // Template rendering supports:
    // - {{value}}, {{valueText}}, {{rowId}}, {{columnId}}, {{label}}, etc
    // - data-* placeholders for actions: data-action="edit"
    const html = tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return escapeHtml(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- Interaction ----
  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];
    const target = e.target;

    // Sort click
    const sortBtn = this._findInPath(path, (el) => el?.dataset?.role === "sort");
    if (sortBtn) {
      e.preventDefault();
      const colId = sortBtn.dataset.col;
      this._toggleSort(colId);
      return;
    }

    // Cell action click (anything with data-action)
    const actionEl = this._findInPath(path, (el) => el?.dataset?.action);
    if (actionEl) {
      const td = this._findInPath(path, (el) => el?.tagName === "TD" && el?.dataset?.row && el?.dataset?.col);
      if (td) {
        e.preventDefault();
        e.stopPropagation();
        const rowId = td.dataset.row;
        const columnId = td.dataset.col;
        const action = actionEl.dataset.action;

        const row = this._rows.find((r) => r.id === rowId) || null;

        this.dispatchEvent(new CustomEvent("ctl-cell-action", {
          bubbles: true,
          composed: true,
          detail: { rowId, columnId, action }
        }));

        return;
      }
    }

    // Row click (ignore if clicking checkbox)
    const isCheckbox = target && target.tagName === "INPUT" && target.type === "checkbox";
    if (isCheckbox) return;

    const tr = this._findInPath(path, (el) => el?.tagName === "TR" && el?.dataset?.row);
    if (tr) {
      const rowId = tr.dataset.row;
      const row = this._rows.find((r) => r.id === rowId) || null;
      this.dispatchEvent(new CustomEvent("ctl-row-click", {
        bubbles: true,
        composed: true,
        detail: { rowId, row }
      }));
    }
  }

  _onChange(e) {
    const el = e.target;
    if (!el || el.tagName !== "INPUT" || el.type !== "checkbox") return;

    // Select all
    if (el.dataset.role === "select-all") {
      const checked = !!el.checked;
      const ids = checked ? this._rows.map((r) => r.id) : [];
      this._selected = ids;
      this.setAttribute("selected", JSON.stringify(this._selected));
      this._renderBody();

      this.dispatchEvent(new CustomEvent("ctl-select", {
        bubbles: true,
        composed: true,
        detail: { selected: [...this._selected], rowId: null, checked }
      }));
      return;
    }

    // Per-row
    if (el.dataset.role === "row-check") {
      const rowId = String(el.dataset.row || "");
      const checked = !!el.checked;

      const next = new Set(this._selected);
      if (checked) next.add(rowId);
      else next.delete(rowId);

      this._selected = Array.from(next);
      this.setAttribute("selected", JSON.stringify(this._selected));

      // update header checkbox indeterminate state
      this._renderHead();

      this.dispatchEvent(new CustomEvent("ctl-select", {
        bubbles: true,
        composed: true,
        detail: { selected: [...this._selected], rowId, checked }
      }));
    }
  }

  _toggleSort(colId) {
    const col = this._columns.find((c) => c.id === colId);
    if (!col || !col.sortable) return;

    const current = this._sort;
    let next;

    if (!current || current.id !== colId) {
      next = { id: colId, dir: "asc" };
    } else {
      next = { id: colId, dir: current.dir === "asc" ? "desc" : "asc" };
    }

    this._sort = next;
    this.setAttribute("sort", JSON.stringify(this._sort));
    this._render();

    this.dispatchEvent(new CustomEvent("ctl-sort", {
      bubbles: true,
      composed: true,
      detail: { sort: this._sort }
    }));
  }

  _findInPath(path, predicate) {
    for (const el of path) {
      if (predicate(el)) return el;
    }
    return null;
  }
  get skeleton() { return this.hasAttribute("skeleton"); }
  set skeleton(v) { v ? this.setAttribute("skeleton", "") : this.removeAttribute("skeleton"); }

  get skeletonRows() {
    const n = Number(this.getAttribute("skeleton-rows") || "8");
    return Number.isFinite(n) ? Math.max(1, Math.min(50, n)) : 8;
  }
  set skeletonRows(v) {
    const n = Number(v);
    this.setAttribute("skeleton-rows", String(Number.isFinite(n) ? n : 8));
  }

}

customElements.define("ctl-table", CtlTable);
